/**
 * Auto Sync Manager
 *
 * Automatically syncs pending data when connectivity is restored
 *
 * @module components/network/auto-sync-manager
 */

'use client';

import { useEffect, useState } from 'react';
import { useConnectivityRestored } from '@/lib/hooks/use-network-status';
import { useSyncQueue } from '@/lib/hooks/use-db';
import { syncQueueOperations } from '@/lib/db';
import type { SyncQueueItem } from '@/lib/db';

// ============================================================================
// Auto Sync Manager Component
// ============================================================================

export function AutoSyncManager() {
  const { pendingCount, refresh } = useSyncQueue();
  const [isSyncing, setIsSyncing] = useState(false);
  const [_lastSyncAttempt, setLastSyncAttempt] = useState<Date | null>(null);
  const [_syncErrors, setSyncErrors] = useState<string[]>([]);

  /**
   * Process sync queue when connectivity is restored
   */
  const processSyncQueue = async () => {
    if (isSyncing) {
      console.log('[AutoSyncManager] Sync already in progress, skipping');
      return;
    }

    if (pendingCount === 0) {
      console.log('[AutoSyncManager] No pending items to sync');
      return;
    }

    try {
      setIsSyncing(true);
      setSyncErrors([]);
      setLastSyncAttempt(new Date());

      console.log('[AutoSyncManager] Starting sync of', pendingCount, 'items');

      // Get all pending items
      const pendingItems = await syncQueueOperations.getPending();

      const errors: string[] = [];

      // Process each item
      for (const item of pendingItems) {
        try {
          // Mark as in progress
          await syncQueueOperations.updateStatus(item.id, 'in_progress');

          // Simulate sync operation (replace with actual API calls)
          // In production, this would call the appropriate API endpoint
          // based on item.entity_type and item.action
          await syncItem(item);

          // Mark as completed
          await syncQueueOperations.updateStatus(item.id, 'completed');
          console.log('[AutoSyncManager] Synced:', item.entity_type, item.entity_id);
        } catch (error) {
          console.error('[AutoSyncManager] Failed to sync item:', item, error);

          // Increment retry count
          await syncQueueOperations.incrementRetry(item.id);

          const errorMessage = `Failed to sync ${item.entity_type} ${item.entity_id}: ${
            error instanceof Error ? error.message : 'Unknown error'
          }`;
          errors.push(errorMessage);

          // Get updated item to check retry count
          const updatedItem = await syncQueueOperations.getByEntity(
            item.entity_type,
            item.entity_id
          );
          if (updatedItem[0] && updatedItem[0].status === 'failed') {
            console.error(
              '[AutoSyncManager] Item exceeded max retries:',
              item.entity_type,
              item.entity_id
            );
          }
        }
      }

      // Clean up completed items
      const cleanedCount = await syncQueueOperations.cleanCompleted();
      console.log('[AutoSyncManager] Cleaned', cleanedCount, 'completed items');

      setSyncErrors(errors);

      // Refresh sync queue
      await refresh();

      if (errors.length === 0) {
        console.log('[AutoSyncManager] Sync completed successfully');
      } else {
        console.log(
          '[AutoSyncManager] Sync completed with',
          errors.length,
          'errors'
        );
      }
    } catch (error) {
      console.error('[AutoSyncManager] Sync process failed:', error);
      setSyncErrors([
        error instanceof Error ? error.message : 'Unknown sync error',
      ]);
    } finally {
      setIsSyncing(false);
    }
  };

  /**
   * Sync individual item (placeholder - replace with actual API calls)
   */
  async function syncItem(_item: SyncQueueItem): Promise<void> {
    // This is a placeholder that simulates API calls
    // In production, implement actual sync logic based on entity type and action

    return new Promise((resolve, reject) => {
      // Simulate network delay
      setTimeout(() => {
        // Simulate 90% success rate for testing
        if (Math.random() > 0.1) {
          resolve();
        } else {
          reject(new Error('Simulated sync failure'));
        }
      }, 500);
    });

    // Production implementation would look like:
    /*
    switch (item.entity_type) {
      case 'job':
        if (item.action === 'update') {
          await fetch(`/api/v1/jobs/${item.entity_id}`, {
            method: 'PUT',
            body: JSON.stringify(item.data),
          });
        }
        break;

      case 'inspection':
        if (item.action === 'create') {
          await fetch('/api/v1/inspections', {
            method: 'POST',
            body: JSON.stringify(item.data),
          });
        }
        break;

      case 'photo':
        if (item.action === 'create') {
          const formData = new FormData();
          formData.append('photo', item.data.blob);
          formData.append('inspection_id', item.data.inspection_id);

          await fetch('/api/v1/storage/upload', {
            method: 'POST',
            body: formData,
          });
        }
        break;
    }
    */
  }

  // Auto-trigger sync when connectivity is restored
  useConnectivityRestored(() => {
    console.log('[AutoSyncManager] Connectivity restored, triggering sync');
    processSyncQueue();
  });

  // Optional: Periodic sync every 5 minutes when online
  useEffect(() => {
    const interval = setInterval(() => {
      if (pendingCount > 0 && !isSyncing) {
        console.log('[AutoSyncManager] Periodic sync check');
        processSyncQueue();
      }
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [pendingCount, isSyncing]);

  // This component doesn't render anything visible
  // It just handles background sync logic
  return null;
}

// ============================================================================
// Sync Status Indicator
// ============================================================================

interface SyncStatusProps {
  className?: string;
}

export function SyncStatus({ className = '' }: SyncStatusProps) {
  const { pendingCount, failedCount } = useSyncQueue();
  const [isSyncing, _setIsSyncing] = useState(false);

  if (pendingCount === 0 && failedCount === 0) {
    return null;
  }

  return (
    <div className={`flex items-center gap-2 text-sm ${className}`}>
      {isSyncing && (
        <div className="flex items-center gap-2 text-blue-600">
          <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
          <span>Syncing...</span>
        </div>
      )}

      {!isSyncing && pendingCount > 0 && (
        <div className="flex items-center gap-1 text-yellow-600">
          <svg
            className="h-4 w-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            {pendingCount} pending
          </span>
        </div>
      )}

      {failedCount > 0 && (
        <div className="flex items-center gap-1 text-red-600">
          <svg
            className="h-4 w-4"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>
            {failedCount} failed
          </span>
        </div>
      )}
    </div>
  );
}
